import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';

import { environment } from '../../../../environments/environment';

import {
  ClientInvitationFilterStatus,
  ClientInvitationResponseModel,
  ClientOnboardingRequestModel,
  ClientReferrersModel,
  OpenClientInvitationModel,
} from '../../model';

@Injectable({
  providedIn: 'root',
})
export class ClientInvitationService {
  // Emits after an invitation was created or re-sent from a place that has no link to the list
  // (e.g. the "Neukunden einladen" modal opened from the header) so the list can reload itself.
  private readonly invitationsChangedSubject: Subject<void> = new Subject<void>();
  public readonly invitationsChanged$: Observable<void> = this.invitationsChangedSubject.asObservable();

  constructor(private http: HttpClient) {}

  public notifyInvitationsChanged(): void {
    this.invitationsChangedSubject.next();
  }

  public inviteClients(body: string[]): Observable<void> {
    return this.http.post<void>(`${environment.apiEndpoint}/account/invite`, body);
  }

  public reinviteClient(clientId: number): Observable<void> {
    return this.http.post<void>(`${environment.apiEndpoint}/account/invite/${clientId}/reinvite`, null);
  }

  public deleteClientInvitation(clientId: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiEndpoint}/account/invite/${clientId}`);
  }

  public getInvitationList(params: {
    filterStatus: ClientInvitationFilterStatus;
    query?: string;
    advisorId?: string;
    page?: number;
    pageSize?: number;
  }): Observable<ClientInvitationResponseModel> {
    let httpParams: HttpParams = new HttpParams().append('filter_status', params.filterStatus);

    if (params.query) {
      httpParams = httpParams.append('query', params.query);
    }

    if (params.advisorId) {
      httpParams = httpParams.append('advisor_id', params.advisorId);
    }

    httpParams = httpParams.append('page', params.page ?? 0);
    httpParams = httpParams.append('page_size', params.pageSize ?? 25);

    return this.http.get<ClientInvitationResponseModel>(`${environment.apiEndpoint}/account/invite/list`, {
      params: httpParams,
    });
  }

  public getOpenInvitations(advisor_id: string): Observable<OpenClientInvitationModel[]> {
    let params: HttpParams = new HttpParams();

    params = params.append('advisor_id', advisor_id);

    return this.http.get<OpenClientInvitationModel[]>(`${environment.apiEndpoint}/account/invite/open`, { params });
  }

  public getClientReferrers(advisor_id: string): Observable<ClientReferrersModel[]> {
    let params: HttpParams = new HttpParams();

    params = params.append('advisor_id', advisor_id);

    return this.http.get<ClientReferrersModel[]>(`${environment.apiEndpoint}/account/invite/referrers`, { params });
  }

  public getLastPersonalInvitationText(): Observable<string> {
    return this.http.get(`${environment.apiEndpoint}/account/advisor/last-personal-invitation-text`, {
      responseType: 'text',
    });
  }

  public inviteOnboarding(body: ClientOnboardingRequestModel): Observable<void> {
    return this.http.post<void>(`${environment.apiEndpoint}/account/client/initiate-onboarding`, body);
  }
}
